import React, {Component} from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { ColorRing } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import moment from "moment";
import {reactLocalStorage as Storage} from "reactjs-localstorage";

import PageTitle from "../../../components/common/PageTitle";
import history from "../../../utils/history";
import {
  Button, ButtonGroup,
  Col, Row,
  Card, CardHeader, CardBody,
  FormSelect, FormInput,
  Modal, ModalHeader, ModalBody
} from "shards-react";

const DAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

// 데이터 종류별 표기 (색/라벨) — 토글 시 색으로도 의미 전달
const TYPE_META = {
  orders:  { label: '주문',  rgb: '90, 106, 240',  color: '#5a6af0' },
  reviews: { label: '리뷰',  rgb: '16, 185, 129',  color: '#10b981' }
};

// 리뷰 채널(fromWrite) 그룹 — 백엔드 FROM_WRITE_GROUP_COND 키와 일치
const CHANNEL_OPTIONS = [
  { key: 'alim',   label: '알림톡' },
  { key: 'widget', label: '위젯' },
  { key: 'sync',   label: '동기화' },
  { key: 'sns',    label: 'SNS' },
  { key: 'etc',    label: '기타' }
];

/**
 * 주문/리뷰 패턴 분석 (슈퍼어드민 전용)
 * - 전체 스토어(기본) 또는 선택 스토어의 주문/리뷰 발생 시간대(0~23) x 요일(월~일) 히트맵
 * - 서비스 기능 고도화 아이디어 도출용 내부 지표 (알림톡 발송 시간대 권장값 근거 등)
 */
@inject(({ AOA }) => ({
  getOrderReviewPatternStats: AOA.getOrderReviewPatternStats,
  getOrderReviewRecentList: AOA.getOrderReviewRecentList,
  getOrderDetail: AOA.getOrderDetail,
  getStoreList: AOA.getStoreList
}))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))

@observer
class OrderReviewPattern extends Component {

  @observable isLoading = true;
  @observable periodDays = 30;          // 30 | 90 | 180
  @observable storeKeyword = '';
  @observable selectedStoreId = '';
  @observable storeOptions = [];
  @observable dataType = 'orders';      // 'orders' | 'reviews' — 히트맵 토글 (재요청 없음)
  @observable result = null;            // { periodDays, startDate, orders:{matrix,total,reviewWrited}, reviews:{matrix,total}, conversionRate }

  // 최근 주문/최신 리뷰 리스트 (기간 필터와 무관한 최신순 N개)
  @observable listLimit = 50;           // 50 | 100 | 200
  @observable recentList = null;        // { orders: [...], reviews: [...] }
  @observable isListLoading = true;
  @observable lastListRefreshAt = null; // 마지막 리스트 갱신 시각 (자동/수동 공통)

  // 주문 상세 모달 (items 내역)
  @observable orderDetailModal = { open: false, loading: false, data: null };

  // 리뷰 채널(fromWrite) 다중 선택 — 리뷰 히트맵/총계/리스트에 적용 (전체 선택 = 필터 없음)
  @observable selectedChannels = CHANNEL_OPTIONS.map(c => c.key);

  // 새로고침 시 필터(기간/스토어/리뷰 채널) 유지용 localStorage 키
  CFG_KEY = 'orderReviewPatternCfg';

  componentDidMount() {
    // 권한 체크 — 슈퍼어드민만 (adminUsageDashboard 와 동일 패턴)
    const me = this.props.me || {};
    if (me.emailId !== 'admin@onnomads.com') {
      toast.error('접근 권한이 없습니다.');
      history.replace('/dashboard');
      return;
    }
    this.restoreFilters(); // 첫 조회 전에 복원해야 복원값으로 조회됨
    this.fetchStoreOptions();
    this.doSearch();
    this.fetchRecentList();

    // 최근 주문/최신 리뷰 30초 자동 갱신 — silent 모드(로딩 표시 없이 조용히 교체), 백그라운드 탭은 스킵
    this.listRefreshTimer = setInterval(() => {
      if (document.hidden) return;
      this.fetchRecentList(true);
    }, 30000);
  }

  componentWillUnmount() {
    if (this.listRefreshTimer) clearInterval(this.listRefreshTimer);
  }

  // === 필터 상태 저장/복원 (새로고침 유지) ===
  @action restoreFilters = () => {
    try {
      const cfg = Storage.getObject(this.CFG_KEY);
      if (cfg && typeof cfg === 'object') {
        const pd = parseInt(cfg.periodDays, 10);
        if ([30, 90, 180].indexOf(pd) > -1) this.periodDays = pd;
        if (typeof cfg.selectedStoreId === 'string') this.selectedStoreId = cfg.selectedStoreId;
        if (typeof cfg.storeKeyword === 'string') this.storeKeyword = cfg.storeKeyword;
        if (Array.isArray(cfg.selectedChannels) && cfg.selectedChannels.length > 0) {
          // 알 수 없는 키(과거 버전 등)는 걸러내고, 유효값이 하나도 없으면 기본(전체) 유지
          const valid = cfg.selectedChannels.filter(k => CHANNEL_OPTIONS.some(c => c.key === k));
          if (valid.length > 0) this.selectedChannels = valid;
        }
      }
    } catch (e) { /* 손상된 저장값은 무시하고 기본값 사용 */ }
  };

  saveFilters = () => {
    try {
      Storage.setObject(this.CFG_KEY, {
        periodDays: this.periodDays,
        selectedStoreId: this.selectedStoreId,
        storeKeyword: this.storeKeyword,
        selectedChannels: this.selectedChannels.slice() // observable array → plain
      });
    } catch (e) {}
  };

  fetchStoreOptions = async () => {
    try {
      const params = {
        page: 1,
        limit: 50,
        searchParams: this.storeKeyword ? { keyword: this.storeKeyword } : {}
      };
      this.props.getStoreList(params, (res) => {
        if (res && res.docs) {
          this.storeOptions = res.docs.map(s => ({
            _id: s._id,
            name: s.name || s.domain || s._id
          }));
        }
      });
    } catch (e) { console.error('fetchStoreOptions', e); }
  };

  @action setPeriod = (days) => {
    if (this.periodDays === days) return;
    this.periodDays = days;
    this.saveFilters();
    this.doSearch();
  };

  @action onChangeStore = (event) => {
    this.selectedStoreId = event.target.value;
    this.saveFilters();
    this.doSearch();
    this.fetchRecentList();
  };

  @action setListLimit = (event) => {
    this.listLimit = parseInt(event.target.value, 10) || 50;
    this.fetchRecentList();
  };

  // 채널 칩 토글 — 마지막 하나는 해제 불가 (빈 선택 방지)
  @action toggleChannel = (key) => {
    const cur = this.selectedChannels.slice();
    const idx = cur.indexOf(key);
    if (idx > -1) {
      if (cur.length === 1) return; // 최소 1개 유지
      cur.splice(idx, 1);
    } else {
      cur.push(key);
    }
    this.selectedChannels = cur;
    this.saveFilters();
    this.doSearch();
    this.fetchRecentList();
  };

  // 전체 선택이면 파라미터 생략 (백엔드 필터 미적용과 동일)
  channelParam = () => (
    this.selectedChannels.length < CHANNEL_OPTIONS.length ? this.selectedChannels.slice() : undefined
  );

  // 필터 적용 중일 때 헤더에 표기할 채널 라벨 (예: "알림톡, 위젯")
  channelFilterLabel = () => {
    if (this.selectedChannels.length >= CHANNEL_OPTIONS.length) return null;
    return CHANNEL_OPTIONS.filter(c => this.selectedChannels.indexOf(c.key) > -1).map(c => c.label).join(', ');
  };

  // 주문 행의 상품(items) 클릭 → 주문 상세 모달 (items 내역 조회)
  @action openOrderDetail = (order) => {
    if (!order || !order._id) return;
    this.orderDetailModal = { open: true, loading: true, data: null };
    this.props.getOrderDetail({ order_id: order._id }, (data) => {
      this.orderDetailModal = { open: true, loading: false, data: data || null };
    });
  };

  @action closeOrderDetail = () => {
    this.orderDetailModal = { open: false, loading: false, data: null };
  };

  // 리스트의 스토어 클릭 → 상단 스토어 선택에 반영하고 해당 스토어 패턴으로 드릴다운
  @action selectStoreFromList = (row) => {
    const store = row && row.store_id;
    if (!store || !store._id) return;
    if (this.selectedStoreId === store._id) return; // 이미 선택된 스토어면 무시
    this.selectedStoreId = store._id;
    this.storeKeyword = store.name || '';
    // 셀렉트 옵션에 없으면 즉시 추가 — 비동기 검색 결과 도착 전 셀렉트가 빈 값으로 보이는 것 방지
    if (!this.storeOptions.some(s => s._id === store._id)) {
      this.storeOptions = [{ _id: store._id, name: store.name || store._id }].concat(this.storeOptions.slice());
    }
    this.fetchStoreOptions(); // 키워드(스토어명) 기준으로 옵션 목록 재구성 (새로고침 복원과도 일관)
    this.saveFilters();
    this.doSearch();
    this.fetchRecentList();
    window.scrollTo({ top: 0, behavior: 'smooth' }); // 상단 히트맵/KPI 확인 동선
  };

  // silent=true: 자동 갱신용 — 로딩 표시 없이 데이터만 교체 (깜빡임 방지)
  @action fetchRecentList = (silent) => {
    if (!silent) this.isListLoading = true;
    this.props.getOrderReviewRecentList(
      { store_id: this.selectedStoreId || undefined, limit: this.listLimit, fromWrites: this.channelParam() },
      (data) => {
        this.recentList = data || null;
        this.isListLoading = false;
        this.lastListRefreshAt = moment();
      }
    );
  };

  @action onChangeStoreKeyword = async (event) => {
    this.storeKeyword = event.target.value;
    this.saveFilters();
    this.fetchStoreOptions();
  };

  @action doSearch = () => {
    this.isLoading = true;
    this.props.getOrderReviewPatternStats(
      { store_id: this.selectedStoreId || undefined, periodDays: this.periodDays, fromWrites: this.channelParam() },
      (data) => {
        // AOA 액션이 {data:...} 래퍼를 언래핑해서 전달함
        this.result = data || null;
        this.isLoading = false;
      }
    );
  };

  formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString();
  };

  // 7x24 매트릭스에서 최대 셀 {day, hour, value}
  findPeakCell = (matrix) => {
    let best = { day: -1, hour: -1, value: 0 };
    (matrix || []).forEach((row, d) => (row || []).forEach((v, h) => {
      if (v > best.value) best = { day: d, hour: h, value: v };
    }));
    return best;
  };

  // 요일 합산 기준 피크 시간(0~23) — 알림톡 발송 시간 제안용
  findPeakHour = (matrix) => {
    const hourly = new Array(24).fill(0);
    (matrix || []).forEach(row => (row || []).forEach((v, h) => { hourly[h] += v; }));
    let best = { hour: -1, value: 0 };
    hourly.forEach((v, h) => { if (v > best.value) best = { hour: h, value: v }; });
    return best;
  };

  peakCellLabel = (matrix) => {
    const p = this.findPeakCell(matrix);
    return p.value > 0 ? `${DAY_LABELS[p.day]} ${String(p.hour).padStart(2, '0')}시` : '-';
  };

  renderKpiCard = (label, value, unit, color) => (
    <Col lg="3" md="6" sm="12" className="mb-3">
      <Card small>
        <CardBody style={{padding: '20px'}}>
          <div style={{fontSize: '13px', color: '#868e96', marginBottom: '6px'}}>{label}</div>
          <div style={{fontSize: '28px', fontWeight: '700', color: color || '#333'}}>
            {value}
            {unit && <span style={{fontSize: '14px', color: '#868e96', marginLeft: '4px'}}>{unit}</span>}
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  // 7행(월~일) x 24열 CSS grid 히트맵 — 값 농도 = 배경 알파, 셀 hover 네이티브 툴팁
  renderHeatmap = (matrix, rgb, unitLabel) => {
    const flat = [].concat(...(matrix || []));
    const max = Math.max(1, ...flat);
    return (
      <div style={{overflowX: 'auto'}}>
        <div style={{display: 'grid', gridTemplateColumns: '34px repeat(24, minmax(24px, 1fr))', gap: '3px', minWidth: '760px'}}>
          {/* 헤더 행: 시간 라벨 */}
          <div/>
          {Array.from({length: 24}, (_, h) => (
            <div key={'h' + h} style={{fontSize: '10px', color: '#868e96', textAlign: 'center'}}>{h}</div>
          ))}
          {/* 요일 행 */}
          {DAY_LABELS.map((day, d) => (
            <React.Fragment key={day}>
              <div style={{fontSize: '11px', color: '#868e96', lineHeight: '24px'}}>{day}</div>
              {(matrix[d] || []).map((v, h) => (
                <div key={h}
                     title={`${day} ${String(h).padStart(2, '0')}시 — ${unitLabel} ${this.formatNumber(v)}건`}
                     style={{
                       height: '24px', borderRadius: '3px',
                       backgroundColor: v > 0 ? `rgba(${rgb}, ${(0.08 + 0.92 * (v / max)).toFixed(3)})` : '#f1f3f5'
                     }}/>
              ))}
            </React.Fragment>
          ))}
        </div>
        {/* 범례 */}
        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '11px', color: '#868e96'}}>
          <span>적음</span>
          {[0.15, 0.35, 0.55, 0.78, 1].map(a => (
            <span key={a} style={{width: '18px', height: '12px', borderRadius: '2px', backgroundColor: `rgba(${rgb}, ${a})`}}/>
          ))}
          <span>많음</span>
          <span style={{marginLeft: '12px'}}>· 셀에 마우스를 올리면 건수가 표시됩니다 (KST 기준)</span>
        </div>
      </div>
    );
  };

  // ===== 최근 주문/최신 리뷰 리스트 =====

  fromWriteLabel = (fw) => {
    if (!fw) return '-';
    if (fw.indexOf('alim') === 0) return '알림톡';
    if (fw.indexOf('widget') === 0) return '위젯';
    if (fw.indexOf('sns') === 0) return 'SNS';
    if (fw === 'cafe24') return '동기화';
    return fw;
  };

  reviewTypeLabel = (t) => {
    switch (t) {
      case 'image': return '포토';
      case 'movie': return '동영상';
      case 'text': return '텍스트';
      default: return t || '-';
    }
  };

  // goodsPt(0~100) → 5점 환산
  starsFromPt = (pt) => {
    if (pt === null || pt === undefined) return '-';
    return (Math.round((pt / 20) * 10) / 10) + '★';
  };

  formatDT = (d) => d ? moment(d).format('MM/DD HH:mm') : '-';

  storeNameOf = (row) => (row && row.store_id && row.store_id.name) || '-';

  listThStyle = { padding: '8px 10px', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', whiteSpace: 'nowrap' };
  listTdStyle = { padding: '7px 10px', borderBottom: '1px solid #f1f3f5', whiteSpace: 'nowrap' };

  renderRecentLists = () => {
    const list = this.recentList;
    const orders = (list && list.orders) || [];
    const reviews = (list && list.reviews) || [];
    const loading = this.isListLoading;

    const emptyRow = (colSpan) => (
      <tr>
        <td colSpan={colSpan} style={{padding: '36px', textAlign: 'center', color: '#aaa'}}>
          {loading ? '데이터 로딩 중...' : '데이터가 없습니다.'}
        </td>
      </tr>
    );

    return (
      <>
        {/* 표시 개수 + 갱신 컨트롤 */}
        <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '10px'}}>
          <span style={{fontSize: '12px', color: '#adb5bd'}}>
            {this.lastListRefreshAt ? `마지막 갱신 ${this.lastListRefreshAt.format('HH:mm:ss')} · ` : ''}
            30초마다 자동 갱신
          </span>
          <Button size="sm" theme="light" disabled={this.isListLoading}
                  onClick={() => this.fetchRecentList()}>
            {this.isListLoading ? '갱신 중...' : '갱신'}
          </Button>
          <span style={{fontSize: '12px', color: '#868e96', marginLeft: '4px'}}>표시 개수</span>
          <FormSelect size="sm" value={this.listLimit} onChange={this.setListLimit} style={{maxWidth: '110px'}}>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value={200}>200개</option>
          </FormSelect>
        </div>

        <Row>
          {/* 최근 주문 */}
          <Col lg="6" md="12" className="mb-3">
            <Card>
              <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0'}}>
                <span style={{fontSize: '14px', fontWeight: '600', color: '#5a6af0'}}>최근 주문</span>
                <span style={{marginLeft: '8px', fontSize: '12px', color: '#868e96'}}>
                  최신순 {orders.length}건 · 기간 필터와 무관
                </span>
              </CardHeader>
              <CardBody style={{padding: 0, maxHeight: '420px', overflowY: 'auto'}}>
                <table style={{width: '100%', fontSize: '12px'}}>
                  <thead>
                    <tr>
                      <th style={this.listThStyle}>일시</th>
                      <th style={this.listThStyle}>스토어</th>
                      <th style={this.listThStyle}>상품명</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>상품</th>
                      <th style={{...this.listThStyle, textAlign: 'right'}}>금액</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>기기</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>리뷰</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? orders.map((o, i) => {
                      const amount = (o.paymentAmount || o.paymentAmount === 0)
                        ? o.paymentAmount : parseInt(o.orderAmount, 10);
                      return (
                        <tr key={o._id || i}>
                          <td style={{...this.listTdStyle, color: '#868e96'}}>{this.formatDT(o.orderAt)}</td>
                          <td style={{...this.listTdStyle, maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', color: '#5a6af0', textDecoration: 'underline'}}
                              title={`${this.storeNameOf(o)} — 클릭하면 이 스토어의 패턴을 조회합니다`}
                              onClick={() => this.selectStoreFromList(o)}>{this.storeNameOf(o)}</td>
                          <td style={{...this.listTdStyle, maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis'}} title={o.orderProductName || ''}>{o.orderProductName || '-'}</td>
                          <td style={{...this.listTdStyle, textAlign: 'center'}}>
                            {o.itemsCount > 0 ? (
                              <span style={{cursor: 'pointer', color: '#5a6af0', textDecoration: 'underline', fontWeight: '600'}}
                                    title="클릭하면 주문 상품 상세 내역을 조회합니다"
                                    onClick={() => this.openOrderDetail(o)}>
                                {o.itemsCount}개
                              </span>
                            ) : '-'}
                          </td>
                          <td style={{...this.listTdStyle, textAlign: 'right', fontWeight: '600'}}>
                            {isNaN(amount) ? '-' : Number(amount).toLocaleString()}
                          </td>
                          <td style={{...this.listTdStyle, textAlign: 'center', color: '#868e96'}}>
                            {o.orderFrom === 'M' ? '모바일' : o.orderFrom === 'P' ? 'PC' : '-'}
                          </td>
                          <td style={{...this.listTdStyle, textAlign: 'center', color: o.reviewWrited ? '#10b981' : '#ced4da', fontWeight: '700'}}>
                            {o.reviewWrited ? '✓' : '-'}
                          </td>
                        </tr>
                      );
                    }) : emptyRow(7)}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>

          {/* 최신 리뷰 */}
          <Col lg="6" md="12" className="mb-3">
            <Card>
              <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0'}}>
                <span style={{fontSize: '14px', fontWeight: '600', color: '#10b981'}}>최신 리뷰</span>
                <span style={{marginLeft: '8px', fontSize: '12px', color: '#868e96'}}>
                  최신순 {reviews.length}건 · 대량등록/복사 제외
                  {this.channelFilterLabel() ? ` · 채널: ${this.channelFilterLabel()}` : ''}
                </span>
              </CardHeader>
              <CardBody style={{padding: 0, maxHeight: '420px', overflowY: 'auto'}}>
                <table style={{width: '100%', fontSize: '12px'}}>
                  <thead>
                    <tr>
                      <th style={this.listThStyle}>일시</th>
                      <th style={this.listThStyle}>스토어</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>채널</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>타입</th>
                      <th style={{...this.listThStyle, textAlign: 'center'}}>별점</th>
                      <th style={this.listThStyle}>내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.length > 0 ? reviews.map((rv, i) => (
                      <tr key={rv._id || i}>
                        <td style={{...this.listTdStyle, color: '#868e96'}}>{this.formatDT(rv.writerAt)}</td>
                        <td style={{...this.listTdStyle, maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', color: '#5a6af0', textDecoration: 'underline'}}
                            title={`${this.storeNameOf(rv)} — 클릭하면 이 스토어의 패턴을 조회합니다`}
                            onClick={() => this.selectStoreFromList(rv)}>{this.storeNameOf(rv)}</td>
                        <td style={{...this.listTdStyle, textAlign: 'center'}}>{this.fromWriteLabel(rv.fromWrite)}</td>
                        <td style={{...this.listTdStyle, textAlign: 'center', color: '#868e96'}}>{this.reviewTypeLabel(rv.reviewType)}</td>
                        <td style={{...this.listTdStyle, textAlign: 'center', color: '#f59e0b', fontWeight: '600'}}>{this.starsFromPt(rv.goodsPt)}</td>
                        <td style={{...this.listTdStyle, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={rv.contents || ''}>
                          {rv.contents || '-'}
                        </td>
                      </tr>
                    )) : emptyRow(6)}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  // 주문 상세 모달 — 기본 정보 + items(주문 상품) 내역
  renderOrderDetailModal = () => {
    const m = this.orderDetailModal;
    const d = m.data;
    const items = (d && Array.isArray(d.items)) ? d.items : [];
    const num = (v) => {
      const n = parseFloat(v);
      return (v === null || v === undefined || v === '' || isNaN(n)) ? '-' : Number(n).toLocaleString();
    };
    const paidLabel = (v) => v === 'T' ? '결제완료' : v === 'F' ? '미결제' : v === 'M' ? '부분결제' : (v || '-');
    const cancelLabel = (v) => v === 'T' ? '전체취소' : v === 'F' ? '정상' : v === 'M' ? '부분취소' : (v || '-');
    const infoCell = (label, value) => (
      <div style={{display: 'flex', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid #f1f3f5'}}>
        <span style={{width: '90px', flexShrink: 0, color: '#868e96'}}>{label}</span>
        <span style={{fontWeight: '500', minWidth: 0}}>{value}</span>
      </div>
    );

    return (
      <Modal size="lg" open={m.open} toggle={this.closeOrderDetail} centered={true}>
        <ModalHeader toggle={this.closeOrderDetail}>
          주문 상세{d && d.platformOrderId ? ` — ${d.platformOrderId}` : ''}
        </ModalHeader>
        <ModalBody style={{maxHeight: '70vh', overflowY: 'auto', padding: '20px'}}>
          {m.loading ? (
            <div style={{padding: '50px 0', textAlign: 'center', color: '#aaa'}}>주문 정보를 불러오는 중...</div>
          ) : !d ? (
            <div style={{padding: '50px 0', textAlign: 'center', color: '#aaa'}}>
              주문 상세를 조회할 수 없습니다.
            </div>
          ) : (
            <>
              {/* 기본 정보 */}
              <Row style={{marginBottom: '16px'}}>
                <Col md="6">
                  {infoCell('주문일시', d.orderAt ? moment(d.orderAt).format('YYYY-MM-DD HH:mm') : '-')}
                  {infoCell('결제일시', d.paymentAt ? moment(d.paymentAt).format('YYYY-MM-DD HH:mm') : '-')}
                  {infoCell('스토어', this.storeNameOf(d))}
                  {infoCell('결제 금액', `${num(d.paymentAmount)}${d.currency ? ' ' + d.currency : '원'}`)}
                </Col>
                <Col md="6">
                  {infoCell('결제 상태', paidLabel(d.paid))}
                  {infoCell('취소 상태', cancelLabel(d.canceled))}
                  {infoCell('주문 기기', d.orderFrom === 'M' ? '모바일' : d.orderFrom === 'P' ? 'PC' : '-')}
                  {infoCell('리뷰 작성', d.reviewWrited
                    ? <span style={{color: '#10b981', fontWeight: '700'}}>작성됨 ✓</span>
                    : <span style={{color: '#adb5bd'}}>미작성</span>)}
                </Col>
              </Row>

              {/* items 테이블 */}
              <div style={{fontSize: '13px', fontWeight: '600', marginBottom: '8px'}}>
                주문 상품 내역 <span style={{color: '#868e96', fontWeight: '400'}}>({items.length}개)</span>
              </div>
              {items.length > 0 ? (
                <table style={{width: '100%', fontSize: '12px'}}>
                  <thead>
                    <tr style={{backgroundColor: '#f8f9fa'}}>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e9ecef', width: '36px'}}>#</th>
                      <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e9ecef'}}>상품명</th>
                      <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e9ecef'}}>옵션</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e9ecef', width: '50px'}}>수량</th>
                      <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #e9ecef', width: '90px'}}>판매가</th>
                      <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #e9ecef', width: '80px'}}>옵션가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{borderBottom: '1px solid #f1f3f5'}}>
                        <td style={{padding: '8px', textAlign: 'center', color: '#868e96'}}>{i + 1}</td>
                        <td style={{padding: '8px'}}>
                          <div style={{fontWeight: '500'}}>{it.product_name || '-'}</div>
                          {it.product_no !== undefined && (
                            <div style={{fontSize: '11px', color: '#adb5bd'}}>상품번호 {it.product_no}{it.product_code ? ` · ${it.product_code}` : ''}</div>
                          )}
                        </td>
                        <td style={{padding: '8px', color: '#495057'}}>{it.option_value || '-'}</td>
                        <td style={{padding: '8px', textAlign: 'center'}}>{it.quantity != null ? it.quantity : '-'}</td>
                        <td style={{padding: '8px', textAlign: 'right'}}>{num(it.product_price)}</td>
                        <td style={{padding: '8px', textAlign: 'right', color: '#868e96'}}>{num(it.option_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '13px', background: '#f8f9fa', borderRadius: '6px'}}>
                  상품 내역이 없습니다.
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    );
  };

  // 인사이트 노트 — 클라이언트 계산 텍스트 (운영 의사결정 참고)
  renderInsights = () => {
    const r = this.result;
    if (!r) return null;
    const orderPeakCell = this.peakCellLabel(r.orders.matrix);
    const reviewPeakCell = this.peakCellLabel(r.reviews.matrix);
    const orderPeakHour = this.findPeakHour(r.orders.matrix);
    const reviewPeakHour = this.findPeakHour(r.reviews.matrix);

    const items = [];
    if (r.orders.total > 0) {
      items.push(`주문 피크: ${orderPeakCell} (요일 합산 기준 ${String(orderPeakHour.hour).padStart(2, '0')}시)`);
    }
    if (r.reviews.total > 0) {
      items.push(`리뷰 작성 피크: ${reviewPeakCell} (요일 합산 기준 ${String(reviewPeakHour.hour).padStart(2, '0')}시)`);
    }
    if (r.orders.total > 0 && r.reviews.total > 0 && orderPeakHour.hour >= 0 && reviewPeakHour.hour >= 0) {
      const diff = reviewPeakHour.hour - orderPeakHour.hour;
      items.push(`리뷰 작성 피크가 주문 피크보다 ${Math.abs(diff)}시간 ${diff >= 0 ? '늦습니다' : '빠릅니다'} — 알림톡 기본 발송 시간대 권장값 설정에 참고`);
    }

    return (
      <Card className="mb-3">
        <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0'}}>
          <span style={{fontSize: '14px', fontWeight: '600'}}>인사이트 노트</span>
        </CardHeader>
        <CardBody style={{padding: '16px 20px'}}>
          <ul style={{margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#495057', lineHeight: 2}}>
            {items.map((t, i) => <li key={i}>{t}</li>)}
            <li style={{color: '#868e96'}}>
              전환율 정의: 기간 내 발생 주문(전체취소 제외) 중 현재 시점까지 리뷰가 작성된(reviewWrited) 비율.
              리뷰 수에서는 대량등록(file)·복사(copy) 리뷰가 제외됩니다.
            </li>
          </ul>
        </CardBody>
      </Card>
    );
  };

  render() {
    const me = this.props.me || {};
    if (me.emailId !== 'admin@onnomads.com') return null;

    const r = this.result;
    const meta = TYPE_META[this.dataType];
    const activeMatrix = r ? (this.dataType === 'orders' ? r.orders.matrix : r.reviews.matrix) : null;
    const activeTotal = r ? (this.dataType === 'orders' ? r.orders.total : r.reviews.total) : 0;

    return (
      <div style={{padding: '0px 16px 30px'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}} className="mb-4">
          <PageTitle title="주문/리뷰 패턴 분석" subtitle="스토어 관리" className="mb-0"/>
        </div>

        {/* === 필터 바 === */}
        <Card className="mb-3">
          <CardBody style={{padding: '20px'}}>
            <Row>
              <Col md="5" className="mb-2">
                <div style={{fontSize: '12px', color: '#868e96', marginBottom: '6px'}}>기간 (오늘 기준 최근)</div>
                <ButtonGroup size="sm">
                  <Button theme={this.periodDays === 30 ? 'primary' : 'light'} onClick={() => this.setPeriod(30)}>30일</Button>
                  <Button theme={this.periodDays === 90 ? 'primary' : 'light'} onClick={() => this.setPeriod(90)}>90일</Button>
                  <Button theme={this.periodDays === 180 ? 'primary' : 'light'} onClick={() => this.setPeriod(180)}>180일</Button>
                </ButtonGroup>
                {r && (
                  <div style={{marginTop: '8px', fontSize: '12px', color: '#868e96'}}>
                    {r.startDate} ~ 오늘 · KST 기준
                  </div>
                )}
              </Col>
              <Col md="7" className="mb-2">
                <div style={{fontSize: '12px', color: '#868e96', marginBottom: '6px'}}>스토어 (전체 또는 특정 스토어 드릴다운)</div>
                <div style={{display: 'flex', gap: '8px'}}>
                  <FormInput
                    size="sm"
                    placeholder="스토어 검색 (이름/도메인)"
                    value={this.storeKeyword}
                    onChange={this.onChangeStoreKeyword}
                    style={{maxWidth: '220px'}}
                  />
                  <FormSelect size="sm" value={this.selectedStoreId} onChange={this.onChangeStore} style={{maxWidth: '320px'}}>
                    <option value="">전체 스토어</option>
                    {this.storeOptions.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </FormSelect>
                  <Button size="sm" theme="light" onClick={() => { this.doSearch(); this.fetchRecentList(); }}>새로고침</Button>
                </div>
              </Col>
            </Row>

            {/* 리뷰 채널(fromWrite) 다중 선택 필터 */}
            <div style={{marginTop: '6px', paddingTop: '14px', borderTop: '1px solid #f1f3f5'}}>
              <div style={{fontSize: '12px', color: '#868e96', marginBottom: '6px'}}>
                리뷰 채널 (다중 선택 — 리뷰 히트맵·총계·리스트에 적용, 주문 데이터는 영향 없음)
              </div>
              <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'}}>
                {CHANNEL_OPTIONS.map(c => {
                  const active = this.selectedChannels.indexOf(c.key) > -1;
                  return (
                    <Button key={c.key} size="sm"
                            theme={active ? 'success' : 'light'}
                            onClick={() => this.toggleChannel(c.key)}>
                      {active ? '✓ ' : ''}{c.label}
                    </Button>
                  );
                })}
                {this.channelFilterLabel() && (
                  <span style={{fontSize: '12px', color: '#10b981', marginLeft: '6px'}}>
                    필터 적용 중: {this.channelFilterLabel()}
                  </span>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* === KPI 카드 === */}
        <Row>
          {this.renderKpiCard('총 주문 수', this.formatNumber(r && r.orders.total), '건', '#5a6af0')}
          {this.renderKpiCard(this.channelFilterLabel() ? `총 리뷰 수 (${this.channelFilterLabel()})` : '총 리뷰 수', this.formatNumber(r && r.reviews.total), '건', '#10b981')}
          {this.renderKpiCard('주문→리뷰 전환율', r ? r.conversionRate : 0, '%', '#f59e0b')}
          {this.renderKpiCard('주문 피크', r ? this.peakCellLabel(r.orders.matrix) : '-', '', '#8b5cf6')}
        </Row>

        {/* === 시간대 x 요일 히트맵 === */}
        <Card className="mb-3">
          <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: '14px', fontWeight: '600'}}>
              시간대 × 요일 히트맵
              <span style={{marginLeft: '8px', fontSize: '12px', fontWeight: '400', color: meta.color}}>
                {meta.label} 기준
                {this.dataType === 'reviews' && this.channelFilterLabel() ? ` · ${this.channelFilterLabel()}` : ''}
              </span>
            </span>
            <ButtonGroup size="sm">
              <Button theme={this.dataType === 'orders' ? 'primary' : 'light'} onClick={() => this.dataType = 'orders'}>
                주문 {r ? `(${this.formatNumber(r.orders.total)})` : ''}
              </Button>
              <Button theme={this.dataType === 'reviews' ? 'success' : 'light'} onClick={() => this.dataType = 'reviews'}>
                리뷰 {r ? `(${this.formatNumber(r.reviews.total)})` : ''}
              </Button>
            </ButtonGroup>
          </CardHeader>
          <CardBody style={{padding: '16px 20px'}}>
            {this.isLoading ? (
              <div className="text-center" style={{padding: '80px 0'}}>
                <ColorRing visible={true} height="50" width="50" colors={['#5a6169', '#5a6169', '#5a6169', '#5a6169', '#5a6169']}/>
              </div>
            ) : (activeMatrix && activeTotal > 0) ? (
              this.renderHeatmap(activeMatrix, meta.rgb, meta.label)
            ) : (
              <div style={{padding: '60px 0', textAlign: 'center', color: '#aaa', fontSize: '13px'}}>
                데이터가 없습니다.
              </div>
            )}
          </CardBody>
        </Card>

        {/* === 인사이트 노트 === */}
        {!this.isLoading && this.renderInsights()}

        {/* === 최근 주문 / 최신 리뷰 리스트 === */}
        {this.renderRecentLists()}

        {/* === 주문 상세 모달 === */}
        {this.renderOrderDetailModal()}

      </div>
    );
  }
}

export default OrderReviewPattern;
